import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import {
  useCreateStockOut,
  useListInventoryItems,
  useListInventoryCategories,
  getListStockOutQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ItemPicker } from "./item-picker";

// Phase 2.1 polish F — internal refill = housekeeping moves coffee/tea/glasses
// from the bulk warehouse to a floor location. From the inventory's perspective
// it's a STOCK-OUT (qty leaves the bulk pile), not a stock-in. No member, no
// charging — just item, qty, source → destination.

const STORAGE_LOCATIONS = ["Pantry", "Store_Room", "IT_Rack", "Cabin_Store", "Reception", "Office_Floor"] as const;

const RefillSchema = z.object({
  transactionDate: z.date({ required_error: "Pick a date" }),
  itemId: z.coerce.number().int().positive("Pick an item"),
  quantity: z.coerce.number().positive("Quantity must be > 0"),
  fromLocation: z.enum(STORAGE_LOCATIONS, { required_error: "Pick a source" }),
  toLocation: z.enum(STORAGE_LOCATIONS, { required_error: "Pick a destination" }),
  reason: z.string().nullable().optional(),
}).superRefine((data, ctx) => {
  if (data.fromLocation === data.toLocation) {
    ctx.addIssue({ code: "custom", path: ["toLocation"], message: "Destination must differ from source" });
  }
});

type FormValues = z.infer<typeof RefillSchema>;

interface Props {
  onSaved?: () => void;
  onDiscard?: () => void;
}

export function RefillForm({ onSaved, onDiscard }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // RECIPE-DISPLAY-03 (Phase 55, Plan 55-01): hide recipe items — they are
  // cooked to order, never "refilled" from inventory.
  //
  // 2026-07-29 — the `stockedInSince: today` gate (added 2026-07-27, "refill
  // fresh deliveries only") was DROPPED. Comp-unlimited pantry stock is bought
  // in bulk occasionally, not daily, so on top of the charging-model filter
  // below it left the picker empty on most days.
  const { data: items = [] } = useListInventoryItems({
    status: "Active",
    excludeRecipe: true,
  });
  const { data: categories = [] } = useListInventoryCategories();
  const createMutation = useCreateStockOut();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // 2026-07-29 — Internal Refill covers Complimentary_Unlimited items only:
  // coffee, tea bags, pantry consumables. Same rule as the General Consumption
  // form (stock-out-form.tsx), and for the same reason — a refill is a free
  // internal move, so a chargeable SKU has no business in this picker. Without
  // this filter the list was every Active item, which after a Pay-Per-Use snack
  // delivery meant the picker showed nothing but sellable F&B.
  const refillableItems = useMemo(() => {
    const compUnlimitedCatIds = new Set(
      categories.filter((c) => c.chargingModel === "Complimentary_Unlimited").map((c) => c.id),
    );
    return items
      .filter((it) => compUnlimitedCatIds.has(it.categoryId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, categories]);

  const form = useForm<FormValues>({
    resolver: zodResolver(RefillSchema),
    defaultValues: {
      transactionDate: new Date(),
      itemId: undefined as unknown as number,
      quantity: undefined as unknown as number,
      fromLocation: "Store_Room",
      toLocation: "Office_Floor",
      reason: null,
    },
  });

  async function onSubmit(values: FormValues) {
    try {
      await createMutation.mutateAsync({
        data: {
          transactionDate: values.transactionDate.toISOString(),
          consumptionType: "Internal_Refill",
          itemId: values.itemId,
          quantityIssued: values.quantity,
          issuedFrom: values.fromLocation,
          toLocation: values.toLocation,
          reason: values.reason ?? null,
        },
      });
      toast({ title: "Refill recorded." });
      queryClient.invalidateQueries({ queryKey: getListStockOutQueryKey() });
      onSaved?.();
    } catch {
      toast({ title: "Could not save. Please try again.", variant: "destructive" });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 px-4 pt-2 pb-6 max-w-md">
        <div>
          <h2 className="text-lg font-semibold">Add Internal Refill</h2>
          <p className="text-xs text-muted-foreground">Housekeeping moves stock from bulk storage to a floor location. No member, no charge.</p>
        </div>

        <FormField
          name="transactionDate"
          control={form.control}
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Date <span className="text-destructive">*</span></FormLabel>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value ? format(field.value, "PPP") : "Pick a date"}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={(d) => { if (d) { field.onChange(d); setDatePickerOpen(false); } }}
                    disabled={(d) => d > new Date()}
                    className="p-4 [--cell-size:2.5rem]"
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="itemId"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Item <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <ItemPicker
                  items={refillableItems}
                  value={field.value ?? null}
                  onChange={(id) => field.onChange(id)}
                  placeholder="Pick an item"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="quantity"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Quantity Refilled <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 20"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-3">
          <FormField
            name="fromLocation"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>From <span className="text-destructive">*</span></FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {STORAGE_LOCATIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="toLocation"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>To <span className="text-destructive">*</span></FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger><SelectValue placeholder="Destination" /></SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {STORAGE_LOCATIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          name="reason"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Optional — e.g. 'Morning refill before 10 am rush'"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value || null)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onDiscard}>Discard</Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? "Saving…" : "Save Refill"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
